/**
 * Route Integration Hook
 * Phase 1: Cockpit Shell + Routing Unification
 * 
 * Bridges SurfaceRouter with WorkspaceContext for URL-based navigation.
 */

import { useEffect, useCallback } from 'react';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { getSurfaceRouter } from './SurfaceRouter';

/**
 * useRouteSync - Syncs workspace state with URL
 *
 * The URL is a side-effect of current state, not the source of truth.
 * On refresh, WorkspaceContext restores the selection from localStorage;
 * the URL is then updated to match (via the listeners below), not the
 * other way around. We only react to the URL on browser back/forward
 * (popstate) — NOT on mount, because doing so raced with localStorage
 * restoration and could silently override a "go to home" intent.
 */
export function useRouteSync(): {
  navigateToPage: (pageId: string) => void;
  getPageUrl: (pageId: string) => string;
} {
  const { currentPageId, selectPage, goToHome, initialized } = useWorkspace();
  const router = getSurfaceRouter();

  // Initialize router with valid surfaces
  useEffect(() => {
    router.initialize(['PageEditor', 'DatabaseView', 'HomePage', 'AIChat', 'QuickNote']);
  }, []);

  // Keep the URL in sync with currentPageId (one-way: state -> URL).
  // Gated on `initialized` so we don't clobber the URL on first render
  // before WorkspaceContext has loaded the saved page from localStorage.
  useEffect(() => {
    if (!initialized) return;
    if (currentPageId) {
      const url = router.buildUrl('PageEditor', { pageId: currentPageId });
      if (router.getCurrentUrl() !== url) {
        router.updateBrowserUrl(url, true);
      }
    } else {
      const url = '/';
      if (router.getCurrentUrl() !== url) {
        router.updateBrowserUrl(url, true);
      }
    }
  }, [currentPageId, initialized]);

  // React to browser back/forward only.
  useEffect(() => {
    const handlePopState = () => {
      const route = router.getCurrentRoute();
      if (route.params.pageId) {
        selectPage(route.params.pageId);
      } else {
        goToHome();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectPage, goToHome]);

  // Navigate to a page and update URL (don't call selectPage - already called by caller)
  const navigateToPage = useCallback((pageId: string) => {
    const url = router.buildUrl('PageEditor', { pageId });
    router.updateBrowserUrl(url, true); // Replace current history entry
    // Note: selectPage is called by the caller (handlePageSelect)
  }, []);

  // Get URL for a page
  const getPageUrl = useCallback((pageId: string): string => {
    return router.buildUrl('PageEditor', { pageId });
  }, []);

  return { navigateToPage, getPageUrl };
}

export default useRouteSync;